import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export const DropdownMenu = ({ children, align = 'end' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, right: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Calculate position when opening
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          left: rect.left
        });
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      {React.Children.map(children, (child) => {
        if (child.type === DropdownMenuTrigger) {
          return React.cloneElement(child, { 
            onClick: () => setIsOpen(!isOpen), 
            ref: triggerRef 
          });
        }
        if (child.type === DropdownMenuContent) {
          return isOpen ? React.cloneElement(child, { 
            align, 
            onClose: () => setIsOpen(false),
            position,
            dropdownRef,
            triggerRef
          }) : null;
        }
        return child;
      })}
    </>
  );
};

export const DropdownMenuTrigger = React.forwardRef(({ children, asChild, onClick }, ref) => {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, { onClick, ref });
  }
  return <div onClick={onClick} ref={ref}>{children}</div>;
});

export const DropdownMenuContent = ({ children, align = 'end', onClose, position, dropdownRef, triggerRef }) => {
  const contentRef = useRef(null);
  const [currentPosition, setCurrentPosition] = React.useState(position);

  useEffect(() => {
    if (dropdownRef) {
      dropdownRef.current = contentRef.current;
    }
  }, [dropdownRef]);

  useEffect(() => {
    setCurrentPosition(position);
  }, [position]);

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!triggerRef?.current) return;
    
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setCurrentPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          left: rect.left
        });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [triggerRef]);

  const content = (
    <div
      ref={contentRef}
      className="fixed z-[9999] min-w-[10rem] bg-card border border-border rounded-lg shadow-xl py-1"
      style={{ 
        position: 'fixed',
        top: `${currentPosition?.top || 0}px`,
        right: align === 'end' ? `${currentPosition?.right || 0}px` : 'auto',
        left: align === 'start' ? `${currentPosition?.left || 0}px` : 'auto',
        zIndex: 9999
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {React.Children.map(children, (child) => {
        if (child && child.type === DropdownMenuItem) {
          return React.cloneElement(child, { onClose });
        }
        return child;
      })}
    </div>
  );

  return createPortal(content, document.body);
};

export const DropdownMenuItem = ({ children, onClick, onClose, className = '' }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    onClick && onClick(e);
    onClose && onClose();
  };

  return (
    <div
      className={`px-2 py-1.5 text-sm text-foreground hover:bg-muted cursor-pointer flex items-center ${className}`}
      onClick={handleClick}
    >
      {children}
    </div>
  );
};

export const DropdownMenuSeparator = () => {
  return <div className="h-px bg-border my-1" />;
};

